import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import type { TransportRouteDetailDto } from '../../../dto/transport';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
  type TransportDeliveryFormItem,
} from '../../transports/components/TransportDeliveryQuantityFields';
import TransportStatusPicker from '../../../components/TransportStatusPicker';
import { transportDatePickerProps } from '../../../components/transportFormUtils';
import { getTransportLabel } from '../../../utils/transportLabel';
import './OrderTransportsSection.css';

export interface OrderTransportStatusPayload {
  status: string;
  actualDelivery?: string | null;
  deliveryItems?: TransportDeliveryFormItem[];
}

/**
 * „Zmień status” of a truck from the ZK card: status, and for „Dostarczony”
 * the actual delivery date and the delivered quantities per line. Shared by
 * the „Transporty i terminy dostarczenia” table and the details drawer; the
 * save goes through orderTransportActions.handleTransportStatusChange.
 */
export default function OrderTransportStatusModal({
  transport,
  onClose,
  onSave,
}: {
  transport: TransportRouteDetailDto | null;
  onClose: () => void;
  /** Resolves true when saved (the modal then closes). */
  onSave: (transportId: string, payload: OrderTransportStatusPayload) => Promise<boolean>;
}) {
  const { t } = useTranslation(['transport', 'common']);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const editedStatus = Form.useWatch('status', form);

  useEffect(() => {
    if (!transport) return;
    form.setFieldsValue({
      status: transport.status,
      actualDelivery: transport.actualDelivery ? dayjs(transport.actualDelivery) : null,
      deliveryItems: buildTransportDeliveryFormItems(transport.cargoItems),
    });
  }, [form, transport]);

  const close = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };
  const submit = async (values: { status: string; actualDelivery?: Dayjs | null; deliveryItems?: TransportDeliveryFormItem[] }) => {
    if (!transport) return;
    setSaving(true);
    try {
      const saved = await onSave(transport.id, {
        status: values.status,
        ...(values.status === 'DELIVERED'
          ? {
              actualDelivery: values.actualDelivery?.format('YYYY-MM-DD') || null,
              deliveryItems: values.deliveryItems || [],
            }
          : { actualDelivery: null }),
      });
      if (saved) {
        form.resetFields();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="order-transports-section__status-modal"
      width={620}
      title={t('transport:orderTransports.changeStatusTitle', { number: getTransportLabel(transport) })}
      open={Boolean(transport)}
      onCancel={close}
      footer={null}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="status" label={t('transport:fields.status')} rules={[{ required: true, message: t('transport:pleaseSelectStatus') }]}>
          <TransportStatusPicker />
        </Form.Item>
        {editedStatus === 'DELIVERED' && (
          <>
            <Form.Item name="actualDelivery" label={t('transport:fields.actualDelivery')} rules={[{ required: true, message: t('transport:validation.actualDeliveryRequired') }]}>
              <DatePicker {...transportDatePickerProps} style={{ width: '100%' }} />
            </Form.Item>
            <div className="order-transports-section__status-hint">
              <InfoCircleOutlined />
              <span>{t('transport:orderTransports.deliveredStatusHint')}</span>
            </div>
            {transport ? <TransportDeliveryQuantityFields cargoItems={transport.cargoItems} routeOrders={transport.routeOrders} /> : null}
          </>
        )}
        <div className="order-transports-section__modal-actions">
          <Button onClick={close}>{t('common:cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={saving}>{t('transport:updateStatus')}</Button>
        </div>
      </Form>
    </Modal>
  );
}
